import { useEffect, useState } from 'react';
import type { PreferenciasMenu } from '../api/operaciones';
import { Icono } from '../componentes/Icono';
import type { EntradaMenu, Menu } from '../logica/navegacion';
import { barraInferior } from '../logica/navegacion';
import {
    MAXIMO_BARRA,
    RESTABLECER_BARRA,
    RESTABLECER_MENU,
    ZONA_MAS,
    cambiarQuitada,
    categoriasParaOrdenar,
    hayBarraGuardada,
    hayMenuGuardado,
    moverEnBarra,
    moverEnCategoria,
    ponerEnBarra,
    quitarDeBarra,
    sePuedeQuitar,
    soltarEnBarra,
    soltarEnZona,
} from '../logica/preferenciasMenu';
import { previsualizar, useArrastre } from './arrastre';
import { dibujoDe } from './iconosMenu';

/**
 * «Personalizar menú» (G129, componentes.md §1g). Cada cambio se guarda en el servidor (`updateMyNavigationPreferences`)
 * y el menú vuelve con él aplicado; mientras se arrastra no se guarda nada. «Restablecer» se apaga si no hay nada
 * guardado (`getMyNavigationPreferences`). Los cambios se anuncian en `aria-live`.
 */
export interface Personalizacion {
    leer: () => Promise<PreferenciasMenu>;
    guardar: (cambios: PreferenciasMenu) => Promise<void>;
}

function useGuardado(p: Personalizacion, abierto: boolean) {
    const [guardadas, setGuardadas] = useState<PreferenciasMenu | null>(null);
    const [vivo, setVivo] = useState('');
    useEffect(() => {
        if (!abierto) {
            return;
        }
        let vigente = true;
        p.leer()
            .then((g) => {
                if (vigente) {
                    setGuardadas(g);
                }
            })
            .catch(() => undefined);
        return () => {
            vigente = false;
        };
    }, [p, abierto]);
    const guardar = (cambios: PreferenciasMenu | null, anuncio: string) => {
        if (cambios === null) {
            return;
        }
        setVivo(anuncio);
        setGuardadas((g) => ({ ...g, ...cambios }));
        void p.guardar(cambios);
    };
    return { guardadas, vivo, guardar };
}

interface Fila {
    clave: string;
    entrada: EntradaMenu;
}

/** Contenido del cajón negro de ordenador: una zona por categoría y «En «Más»» (borde discontinuo). */
export function PersonalizarMenu({ menu, p, abierto, onListo }: { menu: Menu; p: Personalizacion; abierto: boolean; onListo: () => void }) {
    const { guardadas, vivo, guardar } = useGuardado(p, abierto);
    const { arrastre, empezar } = useArrastre((clave, zona, antes) => {
        const e = menu.entradas.find((x) => x.clave === clave);
        guardar(soltarEnZona(menu, clave, zona, antes), zona === ZONA_MAS ? `${e?.etiqueta ?? ''} pasa a «Más»` : `${e?.etiqueta ?? ''} se ha movido`);
    });
    const zonas = [
        ...categoriasParaOrdenar(menu).map((g) => ({ clave: g.clave, titulo: g.titulo, filas: g.entradas.filter((e) => !e.enMas).map((entrada): Fila => ({ clave: entrada.clave, entrada })) })),
        { clave: ZONA_MAS, titulo: 'En «Más»', filas: menu.quitadas.map((entrada): Fila => ({ clave: entrada.clave, entrada })) },
    ];
    const grupoDe = (clave: string) => menu.entradas.find((e) => e.clave === clave)?.grupo ?? null;
    const vistas = previsualizar(zonas, arrastre, (zona) => arrastre !== null && (zona === ZONA_MAS ? sePuedeQuitar({ clave: arrastre.clave }) : zona === grupoDe(arrastre.clave)));

    const mover = (e: EntradaMenu, pasos: number) => {
        const cambios = moverEnCategoria(menu, e.clave, pasos);
        const grupo = categoriasParaOrdenar(menu).find((g) => g.clave === e.grupo);
        const enMenu = grupo?.entradas.filter((x) => !x.enMas) ?? [];
        const desde = enMenu.findIndex((x) => x.clave === e.clave);
        const puesto = Math.max(0, Math.min(enMenu.length - 1, desde + pasos));
        guardar(cambios, `${e.etiqueta}: puesto ${String(puesto + 1)} de ${String(enMenu.length)}`);
    };

    return (
        <>
            <p className="rv-pers__nota">
                Arrastra <i className="icon-grip-vertical" aria-hidden="true" /> o usa las flechas para ordenar cada grupo. Lo que quites va a «Más».
            </p>
            <div className="rv-pers__zonas rv-noscroll">
                {vistas.map((z) => {
                    const esMas = z.clave === ZONA_MAS;
                    const sobre = arrastre !== null && arrastre.zona === z.clave;
                    return (
                        <div key={z.clave} data-rv-zona={z.clave} role="list" aria-label={z.titulo} className={`rv-pers__zona${esMas ? ' rv-pers__zona--mas' : ''}${sobre && esMas ? ' rv-pers__zona--sobre' : ''}`}>
                            <div className="rv-pers__ceja">{z.titulo}</div>
                            {z.filas.map(({ clave, entrada: e }, i) => (
                                <div key={clave} role="listitem" data-rv-fila={clave} className={`rv-pers__fila${arrastre?.clave === clave ? ' rv-pers__fila--arrastrada' : ''}`}>
                                    <button type="button" className="rv-mn-b rv-mn-asa" tabIndex={-1} aria-label={`Arrastrar ${e.etiqueta}`} onPointerDown={(ev) => { empezar(clave, z.clave, ev); }}>
                                        <Icono nombre="grip-vertical" />
                                    </button>
                                    <span className="rv-pers__icono" style={{ color: esMas ? 'rgba(255,255,255,0.6)' : '#fff' }}>
                                        <Icono nombre={dibujoDe(e.icono)} />
                                    </span>
                                    <span className="rv-pers__texto" style={{ fontSize: e.etiqueta.length > 20 ? 13 : 14, color: esMas ? 'rgba(255,255,255,0.75)' : '#fff' }}>
                                        {e.etiqueta}
                                    </span>
                                    {!esMas && (
                                        <>
                                            <button type="button" className="rv-mn-b" disabled={i === 0} aria-label={`Subir ${e.etiqueta}`} onClick={() => { mover(e, -1); }}>
                                                <Icono nombre="arrow-up" />
                                            </button>
                                            <button type="button" className="rv-mn-b" disabled={i === z.filas.length - 1} aria-label={`Bajar ${e.etiqueta}`} onClick={() => { mover(e, 1); }}>
                                                <Icono nombre="arrow-down" />
                                            </button>
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        className="rv-mn-b"
                                        disabled={!esMas && !sePuedeQuitar(e)}
                                        aria-label={esMas ? `Devolver ${e.etiqueta} al menú` : sePuedeQuitar(e) ? `Quitar ${e.etiqueta} del menú (pasa a «Más»)` : 'Inicio no se quita del menú'}
                                        onClick={() => {
                                            guardar(cambiarQuitada(menu, clave, !esMas), esMas ? `${e.etiqueta} vuelve al menú` : `${e.etiqueta} pasa a «Más»`);
                                        }}
                                    >
                                        <Icono nombre={esMas ? 'undo-2' : 'eye-off'} />
                                    </button>
                                </div>
                            ))}
                            {z.filas.length === 0 && esMas && <div className="rv-pers__vacia">Arrastra aquí lo que no uses a diario</div>}
                        </div>
                    );
                })}
            </div>
            <span className="rv-sr" aria-live="polite">{vivo}</span>
            <div className="rv-pers__pie">
                <button type="button" className="rv-pers__restablecer" disabled={!hayMenuGuardado(guardadas)} onClick={() => { guardar(RESTABLECER_MENU, 'Menú restablecido'); }}>
                    <Icono nombre="rotate-ccw" />
                    <span>Restablecer</span>
                </button>
                <button type="button" className="rv-pers__listo" onClick={onListo}>
                    Listo
                </button>
            </div>
        </>
    );
}

/**
 * Hoja del móvil «Tu barra y «Más»» (componentes.md §1g, «Personalizar menú en móvil»): vista previa en vivo de la barra,
 * «En la barra · N de 4» (asa, ↑ ↓ y −) y «En «Más»» (+, apagado con la barra llena). La barra no se queda nunca vacía.
 */
export function PersonalizarBarra({ menu, p, abierto, actual, onListo }: { menu: Menu; p: Personalizacion; abierto: boolean; actual: string | null; onListo: () => void }) {
    const { guardadas, vivo, guardar } = useGuardado(p, abierto);
    const { barra: enBarra, mas } = barraInferior(menu);
    const barra = enBarra.map((e) => e.clave);
    const { arrastre, empezar } = useArrastre((clave, zona, antes) => {
        const e = menu.entradas.find((x) => x.clave === clave);
        guardar(soltarEnBarra(barra, clave, zona === 'barra' ? 'barra' : 'mas', antes), zona === 'barra' ? `${e?.etiqueta ?? ''} va a la barra` : `${e?.etiqueta ?? ''} pasa a «Más»`);
    });
    const enMas = mas.flatMap((g) => g.entradas).filter((e) => !e.noContratada);
    const zonas = [
        { clave: 'barra', filas: enBarra.map((entrada): Fila => ({ clave: entrada.clave, entrada })) },
        { clave: 'mas', filas: enMas.map((entrada): Fila => ({ clave: entrada.clave, entrada })) },
    ];
    const vistas = previsualizar(zonas, arrastre, (zona) => zona === 'mas' ? barra.length > 1 || !barra.includes(arrastre?.clave ?? '') : barra.includes(arrastre?.clave ?? '') || barra.length < MAXIMO_BARRA);
    const [filasBarra, filasMas] = [vistas[0]?.filas ?? [], vistas[1]?.filas ?? []];
    const lleno = barra.length >= MAXIMO_BARRA;

    return (
        <>
            <div className="rv-bp__cuerpo rv-noscroll">
                <div className="rv-bp__titulo">Tu barra y «Más»</div>
                <p className="rv-bp__linea">
                    Hasta 4 secciones en la barra. Arrastra <i className="icon-grip-vertical" aria-hidden="true" /> o usa las flechas.
                </p>
                <div aria-hidden="true" className="rv-bp__previa" style={{ gridTemplateColumns: `repeat(${String(filasBarra.length + 1)}, minmax(0, 1fr))` }}>
                    {filasBarra.map(({ clave, entrada: e }) => (
                        <span key={clave} className="rv-bp__previa-op" style={{ background: clave === actual ? 'var(--rv-red)' : 'transparent' }}>
                            <Icono nombre={dibujoDe(e.icono)} />
                            <span>{e.etiqueta}</span>
                        </span>
                    ))}
                    <span className="rv-bp__previa-op" style={{ color: 'rgba(255,255,255,0.72)' }}>
                        <Icono nombre="ellipsis" />
                        <span>Más</span>
                    </span>
                </div>
                {(['barra', 'mas'] as const).map((zona) => {
                    const filas = zona === 'barra' ? filasBarra : filasMas;
                    const sobre = arrastre !== null && arrastre.zona === zona && zona === 'mas';
                    return (
                        <div key={zona} data-rv-zona={zona} role="list" aria-label={zona === 'barra' ? 'En la barra' : 'En «Más»'} className={`rv-bp__zona${zona === 'mas' ? ' rv-bp__zona--mas' : ''}${sobre ? ' rv-pers__zona--sobre' : ''}`}>
                            <div className="rv-bp__ceja">
                                <span>{zona === 'barra' ? 'En la barra' : 'En «Más»'}</span>
                                <span className="rv-bp__cuenta" style={{ color: zona === 'barra' && lleno ? 'rgba(255,255,255,0.6)' : '#fff' }}>
                                    {zona === 'barra' ? `${String(barra.length)} de 4` : filas.length > 0 ? String(filas.length) : ''}
                                </span>
                            </div>
                            {filas.map(({ clave, entrada: e }, i) => (
                                <div key={clave} role="listitem" data-rv-fila={clave} className={`rv-bp__fila${zona === 'mas' ? ' rv-bp__fila--mas' : ''}${arrastre?.clave === clave ? ' rv-pers__fila--arrastrada' : ''}`}>
                                    <button type="button" className="rv-mn-b rv-mn-asa" tabIndex={-1} style={{ width: 44 }} aria-label={`Arrastrar ${e.etiqueta}`} onPointerDown={(ev) => { empezar(clave, zona, ev); }}>
                                        <Icono nombre="grip-vertical" />
                                    </button>
                                    <span className="rv-bp__icono">
                                        <Icono nombre={dibujoDe(e.icono)} />
                                    </span>
                                    <span className="rv-bp__texto">{e.etiqueta}</span>
                                    {zona === 'barra' && (
                                        <>
                                            <button type="button" className="rv-mn-b" disabled={i === 0} aria-label={`Subir ${e.etiqueta}`} onClick={() => { guardar(moverEnBarra(barra, clave, -1), `${e.etiqueta}: puesto ${String(i)} de ${String(barra.length)}`); }}>
                                                <Icono nombre="arrow-up" />
                                            </button>
                                            <button type="button" className="rv-mn-b" disabled={i === filas.length - 1} aria-label={`Bajar ${e.etiqueta}`} onClick={() => { guardar(moverEnBarra(barra, clave, 1), `${e.etiqueta}: puesto ${String(i + 2)} de ${String(barra.length)}`); }}>
                                                <Icono nombre="arrow-down" />
                                            </button>
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        className="rv-mn-b"
                                        disabled={zona === 'barra' ? barra.length <= 1 : lleno}
                                        aria-label={zona === 'barra' ? `Quitar ${e.etiqueta} de la barra (pasa a «Más»)` : `Poner ${e.etiqueta} en la barra`}
                                        onClick={() => {
                                            guardar(zona === 'barra' ? quitarDeBarra(barra, clave) : ponerEnBarra(barra, clave), zona === 'barra' ? `${e.etiqueta} pasa a «Más»` : `${e.etiqueta} va a la barra`);
                                        }}
                                    >
                                        <Icono nombre={zona === 'barra' ? 'minus' : 'plus'} />
                                    </button>
                                </div>
                            ))}
                            {zona === 'mas' && lleno && filas.length > 0 && <div className="rv-bp__nota">La barra está llena: quita una para poner otra</div>}
                        </div>
                    );
                })}
            </div>
            <span className="rv-sr" aria-live="polite">{vivo}</span>
            <div className="rv-bp__pie">
                <button type="button" className="rv-bp-b rv-pers__restablecer" disabled={!hayBarraGuardada(guardadas)} onClick={() => { guardar(RESTABLECER_BARRA, 'Menú restablecido'); }}>
                    <Icono nombre="rotate-ccw" />
                    <span>Restablecer</span>
                </button>
                <button type="button" className="rv-bp-b rv-pers__listo" onClick={onListo}>
                    Listo
                </button>
            </div>
        </>
    );
}
